import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../utils/api';
import { Outreach, OutreachStats, PaginatedResponse } from '../../types';

// Types
interface OutreachState {
  outreachEmails: Outreach[];
  currentOutreach: Outreach | null;
  stats: OutreachStats | null;
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    pages: number;
    page: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

// Async thunks
export const fetchOutreachEmails = createAsyncThunk(
  'outreach/fetchOutreachEmails',
  async (params: { page?: number; per_page?: number; status?: string; professor_id?: number }, { rejectWithValue }) => {
    try {
      const { page = 1, per_page = 20, status, professor_id } = params;
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        page: page.toString(),
        per_page: per_page.toString()
      });
      
      if (status) {
        queryParams.append('status', status);
      }
      
      if (professor_id) {
        queryParams.append('professor_id', professor_id.toString());
      }
      
      const response = await api.get<PaginatedResponse<Outreach>>(`/outreach?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch outreach emails.'
      );
    }
  }
);

export const fetchOutreachById = createAsyncThunk(
  'outreach/fetchOutreachById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.get<{ outreach: Outreach }>(`/outreach/${id}`);
      return response.data.outreach;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch outreach details.'
      );
    }
  }
);

export const fetchOutreachStats = createAsyncThunk(
  'outreach/fetchOutreachStats',
  async (_, { rejectWithValue }) => {
    try {
      // This would be a custom endpoint that returns statistics about outreach
      const response = await api.get<{ stats: OutreachStats }>('/outreach/stats');
      return response.data.stats;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch outreach statistics.'
      );
    }
  }
);

export const createOutreachEmail = createAsyncThunk(
  'outreach/createOutreachEmail',
  async (outreachData: { professor_id: number; subject: string; email_body: string; notes?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post<{ outreach: Outreach; message: string }>('/outreach', outreachData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create outreach email.'
      );
    }
  }
);

export const sendOutreachEmail = createAsyncThunk(
  'outreach/sendOutreachEmail',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.post<{ outreach: Outreach; message: string }>(`/outreach/${id}/send`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to send outreach email.'
      );
    }
  }
);

export const recordOutreachResponse = createAsyncThunk(
  'outreach/recordOutreachResponse',
  async (params: { id: number; response_type: string; notes?: string }, { rejectWithValue }) => {
    try {
      const { id, ...responseData } = params;
      const response = await api.post<{ outreach: Outreach; message: string }>(`/outreach/${id}/response`, responseData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to record response.'
      );
    }
  }
);

export const generateOutreachEmail = createAsyncThunk(
  'outreach/generateOutreachEmail',
  async (params: { professor_id: number; template_id?: number; custom_fields?: Record<string, string> }, { rejectWithValue }) => {
    try {
      const response = await api.post<{ subject: string; email_body: string; professor: any }>('/outreach/generate', params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to generate outreach email.'
      );
    }
  }
);

// Initial state
const initialState: OutreachState = {
  outreachEmails: [],
  currentOutreach: null,
  stats: null,
  loading: false,
  error: null,
  pagination: {
    total: 0,
    pages: 0,
    page: 1,
    per_page: 20,
    has_next: false,
    has_prev: false
  }
};

// Slice
const outreachSlice = createSlice({
  name: 'outreach',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearCurrentOutreach(state) {
      state.currentOutreach = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch outreach emails
      .addCase(fetchOutreachEmails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOutreachEmails.fulfilled, (state, action: PayloadAction<PaginatedResponse<Outreach>>) => {
        state.loading = false;
        state.outreachEmails = action.payload.items;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOutreachEmails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch outreach by ID
      .addCase(fetchOutreachById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOutreachById.fulfilled, (state, action: PayloadAction<Outreach>) => {
        state.loading = false;
        state.currentOutreach = action.payload;
      })
      .addCase(fetchOutreachById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch outreach stats
      .addCase(fetchOutreachStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOutreachStats.fulfilled, (state, action: PayloadAction<OutreachStats>) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchOutreachStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Create outreach email
      .addCase(createOutreachEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOutreachEmail.fulfilled, (state, action: PayloadAction<{ outreach: Outreach; message: string }>) => {
        state.loading = false;
        state.currentOutreach = action.payload.outreach;
      })
      .addCase(createOutreachEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Send outreach email
      .addCase(sendOutreachEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendOutreachEmail.fulfilled, (state, action: PayloadAction<{ outreach: Outreach; message: string }>) => {
        state.loading = false;
        state.currentOutreach = action.payload.outreach;
        
        // Update the outreach in the list if it exists
        const index = state.outreachEmails.findIndex(o => o.id === action.payload.outreach.id);
        if (index !== -1) {
          state.outreachEmails[index] = action.payload.outreach;
        }
      })
      .addCase(sendOutreachEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Record outreach response
      .addCase(recordOutreachResponse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(recordOutreachResponse.fulfilled, (state, action: PayloadAction<{ outreach: Outreach; message: string }>) => {
        state.loading = false;
        state.currentOutreach = action.payload.outreach;
        
        // Update the outreach in the list if it exists
        const index = state.outreachEmails.findIndex(o => o.id === action.payload.outreach.id);
        if (index !== -1) {
          state.outreachEmails[index] = action.payload.outreach;
        }
      })
      .addCase(recordOutreachResponse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearError, clearCurrentOutreach } = outreachSlice.actions;

export default outreachSlice.reducer;
